/**
 * Pick a destination account (search) and confirm transfer — used for moving services/assets between accounts.
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  TextField,
  CircularProgress,
  Alert,
  Typography,
  Box,
} from '@mui/material';
import { Account } from '../types/account';
import apiService from '../services/api';

export interface MoveToAccountDialogProps {
  open: boolean;
  onClose: () => void;
  /** Current account UUID — excluded from search results */
  excludeAccountId: string;
  title: string;
  subtitle?: string;
  onTransferred: (targetAccountId: string) => Promise<void>;
}

function accountLabel(a: Account): string {
  const name =
    a.display_name ||
    [a.contact_first_name, a.contact_last_name].filter(Boolean).join(' ').trim() ||
    a.organisation_name ||
    'Account';
  const num = a.sonic_account_number ? ` · ${a.sonic_account_number}` : '';
  return `${name}${num}`;
}

const MoveToAccountDialog: React.FC<MoveToAccountDialogProps> = ({
  open,
  onClose,
  excludeAccountId,
  title,
  subtitle,
  onTransferred,
}) => {
  const [value, setValue] = useState<Account | null>(null);
  const [input, setInput] = useState('');
  const [options, setOptions] = useState<Account[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setValue(null);
      setInput('');
      setOptions([]);
      setErr(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const q = input.trim();
    if (q.length < 2) {
      setOptions([]);
      return;
    }
    const timer = window.setTimeout(async () => {
      setLoading(true);
      try {
        const res = await apiService.getAccounts({ q, per_page: 40, page: 1 });
        const list: Account[] = (res.accounts || []).filter((a: Account) => a.id !== excludeAccountId);
        setOptions(list);
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [input, open, excludeAccountId]);

  const handleClose = () => {
    if (!submitting) onClose();
  };

  const handleConfirm = async () => {
    if (!value) return;
    try {
      setSubmitting(true);
      setErr(null);
      await onTransferred(value.id);
      onClose();
    } catch (e: any) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === 'string' ? d : d ? JSON.stringify(d) : e?.message || 'Transfer failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {subtitle}
          </Typography>
        )}
        {err && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr(null)}>
            {err}
          </Alert>
        )}
        <Autocomplete
          value={value}
          onChange={(_, v) => setValue(v)}
          inputValue={input}
          onInputChange={(_, v) => setInput(v)}
          options={options}
          getOptionLabel={(a) => accountLabel(a)}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          filterOptions={(x) => x}
          loading={loading}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search destination account"
              placeholder="Type at least 2 characters"
              helperText="Search by name, email, or Sonic account number"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Cases linked to this service or asset will be re-parented to the destination account.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleConfirm} disabled={!value || submitting}>
          {submitting ? <CircularProgress size={22} /> : 'Move'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MoveToAccountDialog;
